import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    IconLayoutAlignBottom,
    IconLayoutAlignMiddle,
    IconLayoutAlignTop,
    IconPrinter,
    IconRectangle,
    IconRectangleVertical,
    IconRefresh,
    IconRotateClockwise,
    IconWand,
} from '@tabler/icons-react';
import { notifications } from '@/lib/notifications';
import { useTranslation } from '../../../../../i18n/hooks/useTranslation';
import { toBnDigits } from '../../../../../utils/bnFormat';
import { recordToolUsage } from '../../../../../services/toolsService';
import PassportCropModal from '../../components/image/PassportCropModal';
import ToolPageLayout from '../../components/common/ToolPageLayout';
import PhotoBoothInbox from '../../../../../components/common/PhotoBoothInbox';
import useImagePaste from '../../components/image/useImagePaste';
import PassportLivePreview from './PassportLivePreview';
import {
    createPassportPageImage, createPassportPdf, isSupportedImageFile, resolveOrientation,
} from './passportToPdfEngine';
import OutputFormatSelect from '../shared/OutputFormatSelect';
import { PAGE_OUTPUT_FORMATS } from '../shared/imageOutputFormats';
import { passportMaxCopies } from './passportPageConstants';
import { loadPreparedObjectUrl, prepareImageFile } from '../crop/cropImageEngine';
import { downloadBlob } from '../../toolPageUtils';

const MAX_FILE_BYTES = 500 * 1024 * 1024;
const ACCEPT = 'image/*,.jpg,.jpeg,.png,.webp,.avif,.gif,.bmp,.tif,.tiff,.heic,.heif';

const ORIENTATION_OPTIONS = [
    { id: 'auto', labelKey: 'passportToPdf.orientation.auto', Icon: IconWand },
    { id: 'portrait', labelKey: 'passportToPdf.orientation.portrait', Icon: IconRectangleVertical },
    { id: 'landscape', labelKey: 'passportToPdf.orientation.landscape', Icon: IconRectangle },
];

const POSITION_OPTIONS = [
    { id: 'top', labelKey: 'passportToPdf.position.top', Icon: IconLayoutAlignTop },
    { id: 'center', labelKey: 'passportToPdf.position.center', Icon: IconLayoutAlignMiddle },
    { id: 'bottom', labelKey: 'passportToPdf.position.bottom', Icon: IconLayoutAlignBottom },
];

const COPY_OPTIONS = [1, 2, 3, 4];

const ROTATION_OPTIONS = [0, 90, 180, 270];

const DEFAULT_SETTINGS = {
    layout: 'stacked',
    orientation: 'auto',
    vPosition: 'top',
    roundedCorners: false,
    brightness: 100,
    contrast: 100,
    sharpen: 20,
    copies: 1,
    rotation: 0,
    rotationFront: 0,
    rotationBack: 0,
};

function segBtnClass(active) {
    return `flex-1 min-h-[34px] rounded-lg border-none px-2 py-1.5 text-[12.5px] transition-all ${
        active
            ? 'bg-white font-semibold text-amber-600 shadow-[0_1px_2px_rgba(28,24,21,0.06)] dark:bg-zinc-900 dark:text-amber-400'
            : 'bg-transparent font-medium text-[#6B635C] dark:text-zinc-400'
    }`;
}

/* Segmented control container */
function Segmented({ children }) {
    return (
        <div className="flex gap-1 rounded-[11px] border border-[#E8E8EE] bg-[#F7F7F9] p-1 dark:border-zinc-700 dark:bg-zinc-800">
            {children}
        </div>
    );
}

/* Little visual layout icon (two rects, side-by-side or stacked) */
function LayoutGlyph({ row, active }) {
    const color = active ? '#F77B1C' : '#B4B0AA';
    return (
        <span
            className="flex items-center justify-center gap-[3px] rounded"
            style={{ width: 34, height: 22, flexDirection: row ? 'row' : 'column' }}
        >
            <span style={{ background: color, borderRadius: 2, width: row ? 13 : 22, height: row ? 18 : 8 }} />
            <span style={{ background: color, borderRadius: 2, width: row ? 13 : 22, height: row ? 18 : 8 }} />
        </span>
    );
}

/**
 * Passport page outline turned by `deg`. The photo block in one corner is what
 * tells 0° from 180° — a bare rectangle would look identical at both.
 */
function RotationGlyph({ deg, active }) {
    const color = active ? '#F77B1C' : '#B4B0AA';
    return (
        <span className="flex h-[22px] w-[34px] items-center justify-center">
            <svg width="26" height="26" viewBox="0 0 26 26" aria-hidden="true" style={{ transform: `rotate(${deg}deg)` }}>
                <rect x="6" y="2.5" width="14" height="21" rx="2" fill="none" stroke={color} strokeWidth="1.8" />
                <rect x="8.5" y="5" width="7" height="8" rx="1" fill={color} />
                <path d="M8.5 16h9M8.5 19h6" stroke={color} strokeWidth="1.4" strokeLinecap="round" />
            </svg>
        </span>
    );
}

/* Adjustment slider row with a colored value pill */
function AdjustSlider({ label, value, onChange, min, max, step = 1, suffix = '', pillClass, accentClass }) {
    return (
        <div className="space-y-1.5">
            <div className="flex items-center justify-between">
                <span className="text-[12px] font-medium text-[#3A3A42] dark:text-zinc-300">{label}</span>
                <span className={`rounded px-1.5 py-0.5 text-[11px] font-bold tabular-nums ${pillClass}`}>
                    {toBnDigits(value)}{suffix}
                </span>
            </div>
            <input
                type="range"
                min={min}
                max={max}
                step={step}
                value={value}
                onChange={(e) => onChange(Number(e.target.value))}
                className={`w-full ${accentClass}`}
            />
        </div>
    );
}

/** Print a PDF blob through a hidden iframe — same path the NID tool uses. */
function printBlob(blob) {
    const url = URL.createObjectURL(blob);
    const iframe = document.createElement('iframe');
    iframe.style.position = 'fixed';
    iframe.style.right = '0';
    iframe.style.bottom = '0';
    iframe.style.width = '0';
    iframe.style.height = '0';
    iframe.style.border = '0';
    iframe.src = url;
    const cleanup = () => {
        setTimeout(() => {
            URL.revokeObjectURL(url);
            iframe.remove();
        }, 1000);
    };
    iframe.onload = () => {
        try {
            iframe.contentWindow?.focus();
            iframe.contentWindow?.print();
        } catch { /* ignore */ }
        window.addEventListener('focus', cleanup, { once: true });
        setTimeout(cleanup, 120000);
    };
    document.body.appendChild(iframe);
}

export default function PassportToPdfImage() {
    const { t } = useTranslation();
    const frontInputRef = useRef(null);
    const backInputRef = useRef(null);

    const [frontFile, setFrontFile] = useState(null);
    const [backFile, setBackFile] = useState(null);
    const [frontSourceFile, setFrontSourceFile] = useState(null);
    const [backSourceFile, setBackSourceFile] = useState(null);
    // Left-column tabs: 'settings' = the tool controls, 'booth' = the photo-booth
    // queue (tab exists only while the booth is ON). Booth photos already loaded
    // into the page slots stay checked; marks reset when both slots empty again.
    const [sideTab, setSideTab] = useState('settings');
    const [boothOn, setBoothOn] = useState(false);
    const [boothMarkedIds, setBoothMarkedIds] = useState([]);
    useEffect(() => { if (!frontFile && !backFile) setBoothMarkedIds([]); }, [frontFile, backFile]);
    // handleCropApply is declared before the booth-queue helpers — bridge via a ref.
    const advanceBoothQueueRef = useRef(() => {});
    const [settings, setSettings] = useState(DEFAULT_SETTINGS);
    const [draggingSlot, setDraggingSlot] = useState(null);
    const [processing, setProcessing] = useState(false);
    const [printing, setPrinting] = useState(false);
    const [result, setResult] = useState(null);
    // What the run button hands over: the PDF, or the same sheet as a picture.
    // Printing always goes through the PDF — that is the sheet a printer lays out.
    const [outputFormat, setOutputFormat] = useState('pdf');
    const [cropModal, setCropModal] = useState(null);
    const [cropBusy, setCropBusy] = useState(false);

    const updateSettings = useCallback((patch) => {
        setSettings((prev) => ({ ...prev, ...patch }));
        setResult(null);
    }, []);

    const effectiveOrientation = useMemo(
        () => resolveOrientation(settings.layout, settings.orientation, settings.copies),
        [settings.layout, settings.orientation, settings.copies],
    );

    const portraitOnly = settings.copies > 2;
    const maxCopies = passportMaxCopies(settings.rotation);

    useEffect(() => {
        if (portraitOnly && settings.orientation !== 'portrait') {
            updateSettings({ orientation: 'portrait' });
        }
    }, [portraitOnly, settings.orientation, updateSettings]);

    // Turning the pages drops the ceiling, so a page already set higher steps down.
    useEffect(() => {
        if (settings.copies > maxCopies) updateSettings({ copies: maxCopies });
    }, [maxCopies, settings.copies, updateSettings]);

    // A passport page is 125 mm tall — two of them stacked are 250 mm, more than
    // a landscape A4's 210 mm short edge. Sideways, the only layout that fits is
    // side by side, so picking landscape picks it too.
    const landscapeOnlySideBySide = settings.orientation === 'landscape';

    useEffect(() => {
        if (landscapeOnlySideBySide && settings.layout !== 'sideBySide') {
            updateSettings({ layout: 'sideBySide' });
        }
    }, [landscapeOnlySideBySide, settings.layout, updateSettings]);

    const visibleOrientationOptions = useMemo(
        () => ORIENTATION_OPTIONS.filter((opt) => (portraitOnly ? opt.id === 'portrait' : true)),
        [portraitOnly],
    );

    const previewSettings = useMemo(
        () => ({ ...settings, orientation: effectiveOrientation }),
        [settings, effectiveOrientation],
    );

    const adjustmentsActive = settings.brightness !== 100
        || settings.contrast !== 100
        || settings.sharpen !== 20;

    const resetAdjustments = () => updateSettings({ brightness: 100, contrast: 100, sharpen: 20 });

    const pickFile = useCallback((incoming) => {
        const file = Array.from(incoming || [])[0];
        if (!file) return null;
        if (!isSupportedImageFile(file)) {
            notifications.show({ title: t('common.error'), message: t('passportToPdf.errors.invalidType'), color: 'red' });
            return null;
        }
        if (file.size > MAX_FILE_BYTES) {
            notifications.show({ title: t('common.error'), message: t('passportToPdf.errors.tooLarge'), color: 'red' });
            return null;
        }
        return file;
    }, [t]);

    const assignFile = useCallback((slot, file) => {
        if (!file) return;
        if (slot === 'front') setFrontFile(file);
        else setBackFile(file);
        setResult(null);
    }, []);

    const closeCropModal = useCallback(() => {
        setCropModal((prev) => {
            if (prev?.src) URL.revokeObjectURL(prev.src);
            return null;
        });
        setCropBusy(false);
    }, []);

    const openCropModal = useCallback(async (slot, file, sourceFile = file) => {
        try {
            const src = await loadPreparedObjectUrl(file);
            setCropModal({ slot, file, sourceFile, src });
        } catch {
            notifications.show({ title: t('common.error'), message: t('passportToPdf.crop.loadFailed'), color: 'red' });
        }
    }, [t]);

    const handleCropApply = useCallback(async (blob) => {
        if (!cropModal) return;
        setCropBusy(true);
        try {
            const base = cropModal.sourceFile.name.replace(/\.[^.]+$/, '');
            const cropped = new File([blob], `${base}-cropped.jpg`, { type: 'image/jpeg' });
            assignFile(cropModal.slot, cropped);
            if (cropModal.slot === 'front') setFrontSourceFile(cropModal.sourceFile);
            else setBackSourceFile(cropModal.sourceFile);
            closeCropModal();
            advanceBoothQueueRef.current(); // booth queue: crop the next photo (no-op otherwise)
        } finally {
            setCropBusy(false);
        }
    }, [assignFile, closeCropModal, cropModal]);

    /** Upload → straighten what the browser can → crop the page to 88×125. */
    const handleFileInput = useCallback(async (slot, files) => {
        const picked = pickFile(files);
        if (!picked) return;
        let prepared = picked;
        try {
            prepared = await prepareImageFile(picked);
        } catch {
            prepared = picked;
        }
        await openCropModal(slot, prepared);
    }, [openCropModal, pickFile]);

    // ── Booth crop queue ─────────────────────────────────────────────────────
    // After সিলেক্ট the chosen photos run the SAME per-photo crop modal one by one
    // (front page first, then back); each apply marks its booth photo, and when the
    // queue drains the column returns to সেটিংস. Closing the modal aborts the rest.
    const boothQueueRef = useRef([]);      // remaining [{ file, id, slot }]
    const boothActiveIdRef = useRef(null); // booth id of the photo now in the crop modal
    const startNextBoothCrop = useCallback(() => {
        const next = boothQueueRef.current.shift();
        if (!next) { setSideTab('settings'); return; }
        boothActiveIdRef.current = next.id;
        void handleFileInput(next.slot, [next.file]);
    }, [handleFileInput]);
    const advanceBoothQueue = useCallback(() => {
        const id = boothActiveIdRef.current;
        if (id == null) return;
        boothActiveIdRef.current = null;
        setBoothMarkedIds(prev => (prev.includes(id) ? prev : [...prev, id]));
        startNextBoothCrop();
    }, [startNextBoothCrop]);
    advanceBoothQueueRef.current = advanceBoothQueue;
    const abortBoothQueue = useCallback(() => {
        boothQueueRef.current = [];
        boothActiveIdRef.current = null;
    }, []);
    // Booth multi-pick: the first free slots (front, then back) take the first
    // selections; with both pages already set, one pick replaces the back page.
    const handleBoothPickMany = useCallback((files, ids) => {
        const slots = [];
        if (!frontFile) slots.push('front');
        if (!backFile) slots.push('back');
        if (!slots.length) slots.push('back');
        const pairs = files.slice(0, slots.length).map((f, i) => ({ file: f, id: ids[i], slot: slots[i] }));
        if (!pairs.length) return;
        boothQueueRef.current = pairs;
        startNextBoothCrop();
    }, [frontFile, backFile, startNextBoothCrop]);

    // Ctrl/Cmd+V fills whichever slot is still empty.
    useImagePaste(useCallback((files) => {
        void handleFileInput(frontFile ? 'back' : 'front', files);
    }, [frontFile, handleFileInput]));

    const openPicker = (slot) => {
        if (slot === 'front') frontInputRef.current?.click();
        else backInputRef.current?.click();
    };

    // ── Preview slot handlers ──
    const onDrop = (slot, e) => {
        e.preventDefault();
        setDraggingSlot(null);
        void handleFileInput(slot, e.dataTransfer.files);
    };
    const onDragOver = (slot, e) => { e.preventDefault(); setDraggingSlot(slot); };
    const onDragLeave = () => setDraggingSlot(null);
    const onCropSlot = (slot) => {
        const src = slot === 'front' ? (frontSourceFile || frontFile) : (backSourceFile || backFile);
        if (src) void openCropModal(slot, src, src);
    };
    const onRemoveSlot = (slot) => {
        if (slot === 'front') { setFrontFile(null); setFrontSourceFile(null); }
        else { setBackFile(null); setBackSourceFile(null); }
        updateSettings(slot === 'front' ? { rotationFront: 0 } : { rotationBack: 0 });
        setResult(null);
    };
    const onSwap = () => {
        setFrontFile(backFile);
        setBackFile(frontFile);
        setFrontSourceFile(backSourceFile);
        setBackSourceFile(frontSourceFile);
        // The turn belongs to the photo, not the slot, so it rides along with it.
        updateSettings({ rotationFront: settings.rotationBack, rotationBack: settings.rotationFront });
        setResult(null);
    };
    const onDuplicate = () => {
        if (settings.copies >= maxCopies) return;
        updateSettings({ copies: settings.copies + 1 });
    };
    const onRotate = () => updateSettings({ rotation: ((settings.rotation || 0) + 90) % 360 });
    const onRotateSlot = (slot) => {
        const key = slot === 'front' ? 'rotationFront' : 'rotationBack';
        updateSettings({ [key]: ((settings[key] || 0) + 90) % 360 });
    };

    const handleReset = () => {
        setFrontFile(null);
        setBackFile(null);
        setFrontSourceFile(null);
        setBackSourceFile(null);
        setSettings(DEFAULT_SETTINGS);
        setResult(null);
    };

    const canRun = Boolean(frontFile && backFile);

    const buildPdf = useCallback(async () => {
        if (result) return result;
        const blob = await createPassportPdf(frontFile, backFile, previewSettings);
        setResult(blob);
        return blob;
    }, [result, frontFile, backFile, previewSettings]);

    const handleCreate = async () => {
        if (!canRun || processing) return;
        recordToolUsage('image/passport-to-pdf', { category: 'image' });
        setProcessing(true);
        try {
            if (outputFormat === 'pdf') {
                const blob = await buildPdf();
                downloadBlob(blob, 'passport.pdf');
            } else {
                // `ext` is what the file REALLY is: a browser that cannot write the
                // chosen format falls back inside encodeCanvas, and naming the file
                // after the fallback keeps it openable.
                const out = await createPassportPageImage(frontFile, backFile, previewSettings, outputFormat);
                downloadBlob(out.blob, `passport.${out.ext}`);
            }
            notifications.show({ title: t('common.success'), message: t('passportToPdf.done'), color: 'green' });
        } catch {
            notifications.show({ title: t('common.error'), message: t('passportToPdf.errors.failed'), color: 'red' });
        } finally {
            setProcessing(false);
        }
    };

    const handlePrint = async () => {
        if (!canRun || printing) return;
        recordToolUsage('image/passport-to-pdf', { category: 'image' });
        setPrinting(true);
        try {
            const blob = await buildPdf();
            printBlob(blob);
        } catch {
            notifications.show({ title: t('common.error'), message: t('passportToPdf.errors.failed'), color: 'red' });
        } finally {
            setPrinting(false);
        }
    };

    const status = canRun ? 'ready' : 'empty';
    const statusText = canRun ? t('passportToPdf.readyHint') : t('passportToPdf.previewEmpty');

    const cropModalTitle = cropModal?.slot === 'front'
        ? t('passportToPdf.frontTitle')
        : cropModal?.slot === 'back' ? t('passportToPdf.backTitle') : '';

    const copiesLabel = settings.copies > 1
        ? t('passportToPdf.copiesFooter', { count: toBnDigits(settings.copies) })
        : t('passportToPdf.copiesFooterOne');

    return (
        <>
        <ToolPageLayout
            slug="image/passport-to-pdf"
            subtitleKey="passportToPdf.subtitle"
            status={status}
            statusText={statusText}
            // The সেটিংস/ফটো বুথ tab bar carries its own labels — the card header only
            // shows while the booth is OFF (no tabs) to avoid a duplicate "সেটিংস".
            controlsTitle={boothOn ? undefined : t('passportToPdf.settingsTitle')}
            stickyPreview
            showTrust={false}
            onReset={frontFile || backFile ? handleReset : undefined}
            primaryAction={{
                label: outputFormat === 'pdf'
                    ? t('passportToPdf.create')
                    : t('toolPage.downloadAs', { format: t(`toolPage.outputFormat.formats.${outputFormat}`) }),
                onClick: handleCreate,
                disabled: !canRun || processing,
                loading: processing,
            }}
            // Print belongs with the run button, not buried at the foot of the
            // settings column — it is the other thing you do with a finished sheet.
            secondaryAction={{
                key: 'print',
                label: printing ? t('passportToPdf.printing') : t('passportToPdf.print'),
                icon: IconPrinter,
                onClick: handlePrint,
                disabled: !canRun || printing,
            }}
            actionExtra={(
                <OutputFormatSelect
                    formats={PAGE_OUTPUT_FORMATS}
                    value={outputFormat}
                    onChange={setOutputFormat}
                    disabled={processing}
                />
            )}
            controls={(
                <div className="space-y-4">
                    <input ref={frontInputRef} type="file" accept={ACCEPT} className="hidden"
                        onChange={(e) => { void handleFileInput('front', e.target.files); e.target.value = ''; }} />
                    <input ref={backInputRef} type="file" accept={ACCEPT} className="hidden"
                        onChange={(e) => { void handleFileInput('back', e.target.files); e.target.value = ''; }} />

                    {/* সেটিংস / ফটো বুথ tabs — booth tab only while the booth is ON. Selected
                        photos crop one by one and land in the page slots of the preview. */}
                    {boothOn && (
                        <div className="grid grid-cols-2 gap-1.5 p-1 rounded-lg bg-zinc-100 dark:bg-zinc-800">
                            {[
                                { id: 'settings', label: t('passportToPdf.settingsTitle') },
                                { id: 'booth', label: t('photoCollectorPanel.title') },
                            ].map((tb) => (
                                <button key={tb.id} type="button" onClick={() => setSideTab(tb.id)}
                                    className={`py-1.5 rounded-md text-xs font-semibold transition-colors ${sideTab === tb.id ? 'bg-white dark:bg-zinc-900 text-primary shadow-sm' : 'text-zinc-500 dark:text-zinc-400 hover:text-zinc-700 dark:hover:text-zinc-200'}`}>
                                    {tb.label}
                                </button>
                            ))}
                        </div>
                    )}
                    {/* Booth pane — kept mounted (hidden) so the on/off status resolves */}
                    <div className={boothOn && sideTab === 'booth' ? undefined : 'hidden'}>
                        <PhotoBoothInbox multiSelect markedIds={boothMarkedIds} onPickFiles={handleBoothPickMany} onStatusChange={setBoothOn} />
                    </div>
                    <div className={boothOn && sideTab === 'booth' ? 'hidden' : 'space-y-4'}>
                    {/* ── Page settings ── */}
                    <div className="space-y-2">
                        <div className="flex items-center justify-between">
                            <span className="text-[12.5px] font-semibold text-[#1B1B1F] dark:text-zinc-200">{t('passportToPdf.pageTitle')}</span>
                            <span className="text-[11px] font-medium text-[#9A9AA3] dark:text-zinc-500">{t('passportToPdf.pageLabel')}</span>
                        </div>
                        <Segmented>
                            {/* Icon only; the name lives in the tooltip and the
                                accessible label so the row stays compact. */}
                            {visibleOrientationOptions.map((opt) => (
                                <button
                                    key={opt.id}
                                    type="button"
                                    onClick={() => updateSettings({ orientation: opt.id })}
                                    title={t(opt.labelKey)}
                                    aria-label={t(opt.labelKey)}
                                    className={`${segBtnClass(settings.orientation === opt.id)} flex items-center justify-center`}
                                >
                                    <opt.Icon size={18} stroke={1.9} />
                                </button>
                            ))}
                        </Segmented>
                        {portraitOnly ? (
                            <p className="text-[11px] text-[#A8A099] dark:text-zinc-500">{t('passportToPdf.portraitOnlyHint')}</p>
                        ) : null}
                    </div>

                    {/* ── Layout ── */}
                    <div className="space-y-2 border-t border-[#F0EEEB] pt-3.5 dark:border-zinc-800">
                        <div className="flex items-center justify-between">
                            <span className="text-[12.5px] font-semibold text-[#1B1B1F] dark:text-zinc-200">{t('passportToPdf.layoutTitle')}</span>
                            <button
                                type="button"
                                onClick={() => updateSettings({ layout: settings.layout === 'sideBySide' ? 'stacked' : 'sideBySide' })}
                                disabled={landscapeOnlySideBySide}
                                className="inline-flex items-center gap-1 text-[11px] font-semibold text-primary transition-opacity disabled:cursor-default disabled:opacity-40"
                            >
                                <IconRefresh size={12} stroke={2} />
                                {t('passportToPdf.layoutReset')}
                            </button>
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                            {[{ id: 'sideBySide', row: true }, { id: 'stacked', row: false }].map((opt) => {
                                const active = settings.layout === opt.id;
                                // Stacked cannot fit a landscape sheet, so it is off
                                // the table rather than silently overflowing.
                                const disabled = landscapeOnlySideBySide && opt.id !== 'sideBySide';
                                return (
                                    <button
                                        key={opt.id}
                                        type="button"
                                        onClick={() => updateSettings({ layout: opt.id })}
                                        disabled={disabled}
                                        title={disabled
                                            ? t('passportToPdf.landscapeSideBySideHint')
                                            : t(`passportToPdf.layout.${opt.id}`)}
                                        aria-label={t(`passportToPdf.layout.${opt.id}`)}
                                        className={`flex items-center justify-center rounded-xl border px-2 py-2.5 transition-colors disabled:cursor-not-allowed disabled:opacity-40 ${
                                            active
                                                ? 'border-primary bg-[#FFF4E6] dark:border-primary dark:bg-primary/10'
                                                : 'border-[#ECE8E4] bg-white hover:border-primary/40 dark:border-zinc-700 dark:bg-zinc-900'
                                        }`}
                                    >
                                        <LayoutGlyph row={opt.row} active={active} />
                                    </button>
                                );
                            })}
                        </div>
                        {landscapeOnlySideBySide ? (
                            <p className="text-[11px] text-[#A8A099] dark:text-zinc-500">
                                {t('passportToPdf.landscapeSideBySideHint')}
                            </p>
                        ) : null}
                    </div>

                    {/* ── Rotate (applies to BOTH pages together) ── */}
                    <div className="space-y-2 border-t border-[#F0EEEB] pt-3.5 dark:border-zinc-800">
                        <div className="flex items-center justify-between">
                            <span className="text-[12.5px] font-semibold text-[#1B1B1F] dark:text-zinc-200">{t('passportToPdf.rotateTitle')}</span>
                            <button
                                type="button"
                                onClick={onRotate}
                                className="inline-flex items-center gap-1 text-[11px] font-semibold text-primary"
                            >
                                <IconRotateClockwise size={12} stroke={2} />
                                {t('passportToPdf.rotate90')}
                            </button>
                        </div>
                        <Segmented>
                            {ROTATION_OPTIONS.map((deg) => (
                                <button
                                    key={deg}
                                    type="button"
                                    onClick={() => updateSettings({ rotation: deg })}
                                    title={`${toBnDigits(deg)}°`}
                                    aria-label={`${toBnDigits(deg)}°`}
                                    className={`${segBtnClass(settings.rotation === deg)} flex items-center justify-center`}
                                >
                                    <RotationGlyph deg={deg} active={settings.rotation === deg} />
                                </button>
                            ))}
                        </Segmented>
                        <p className="text-[11px] text-[#A8A099] dark:text-zinc-500">{t('passportToPdf.rotateHint')}</p>
                    </div>

                    {/* ── Image position (single copy — top / center / bottom on the page) ── */}
                    {settings.copies === 1 ? (
                        <div className="space-y-2 border-t border-[#F0EEEB] pt-3.5 dark:border-zinc-800">
                            <span className="text-[12.5px] font-semibold text-[#1B1B1F] dark:text-zinc-200">{t('passportToPdf.positionTitle')}</span>
                            <Segmented>
                                {POSITION_OPTIONS.map((opt) => (
                                    <button
                                        key={opt.id}
                                        type="button"
                                        onClick={() => updateSettings({ vPosition: opt.id })}
                                        title={t(opt.labelKey)}
                                        aria-label={t(opt.labelKey)}
                                        className={`${segBtnClass(settings.vPosition === opt.id)} flex items-center justify-center`}
                                    >
                                        <opt.Icon size={18} stroke={1.9} />
                                    </button>
                                ))}
                            </Segmented>
                        </div>
                    ) : null}

                    {/* ── Copies ── */}
                    <div className="space-y-2 border-t border-[#F0EEEB] pt-3.5 dark:border-zinc-800">
                        <span className="text-[12.5px] font-semibold text-[#1B1B1F] dark:text-zinc-200">{t('passportToPdf.copiesTitle')}</span>
                        <Segmented>
                            {COPY_OPTIONS.filter((count) => count <= maxCopies).map((count) => (
                                <button key={count} type="button" onClick={() => updateSettings({ copies: count })} className={segBtnClass(settings.copies === count)}>
                                    {toBnDigits(count)}
                                </button>
                            ))}
                        </Segmented>
                        {maxCopies < COPY_OPTIONS.length && (
                            <p className="text-[11px] text-[#A8A099] dark:text-zinc-500">{t('passportToPdf.copiesRotatedHint')}</p>
                        )}
                    </div>

                    {/* ── Rounded corners ── */}
                    <label className="flex cursor-pointer items-center justify-between gap-3 border-t border-[#F0EEEB] pt-3.5 dark:border-zinc-800">
                        <span>
                            <span className="block text-[12.5px] font-semibold text-[#1B1B1F] dark:text-zinc-200">{t('passportToPdf.roundedCorners')}</span>
                            <span className="mt-0.5 block text-[10px] font-normal text-[#9A9AA3] dark:text-zinc-500">{t('passportToPdf.roundedCornersHint')}</span>
                        </span>
                        <button
                            type="button"
                            role="switch"
                            aria-checked={settings.roundedCorners}
                            onClick={() => updateSettings({ roundedCorners: !settings.roundedCorners })}
                            className={`relative h-[22px] w-10 flex-none rounded-full transition-colors ${settings.roundedCorners ? 'bg-primary' : 'bg-[#D6D6DE] dark:bg-zinc-600'}`}
                        >
                            <span className={`absolute top-[3px] block h-4 w-4 rounded-full bg-white shadow transition-[left] ${settings.roundedCorners ? 'left-[21px]' : 'left-[3px]'}`} />
                        </button>
                    </label>

                    {/* ── Image adjustments (applied while the page is drawn) ── */}
                    <div className="space-y-3 border-t border-[#F0EEEB] pt-3.5 dark:border-zinc-800">
                        <div className="flex items-center justify-between">
                            <span className="text-[12.5px] font-semibold text-[#1B1B1F] dark:text-zinc-200">
                                {t('passportToPdf.adjustTitle')}
                            </span>
                            <button
                                type="button"
                                onClick={resetAdjustments}
                                disabled={!adjustmentsActive}
                                className="text-[11px] font-semibold text-primary transition-opacity disabled:cursor-default disabled:opacity-40"
                            >
                                {t('passportToPdf.resetAdjustments')}
                            </button>
                        </div>
                        <AdjustSlider
                            label={t('passportToPdf.brightness', { value: toBnDigits(settings.brightness) })}
                            value={settings.brightness}
                            onChange={(v) => updateSettings({ brightness: v })}
                            min={50}
                            max={150}
                            pillClass="bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-300"
                            accentClass="accent-amber-500"
                        />
                        <AdjustSlider
                            label={t('passportToPdf.contrast', { value: toBnDigits(settings.contrast) })}
                            value={settings.contrast}
                            onChange={(v) => updateSettings({ contrast: v })}
                            min={50}
                            max={150}
                            pillClass="bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-300"
                            accentClass="accent-amber-500"
                        />
                        <AdjustSlider
                            label={t('passportToPdf.sharpen', { value: toBnDigits(settings.sharpen) })}
                            value={settings.sharpen}
                            onChange={(v) => updateSettings({ sharpen: v })}
                            min={0}
                            max={40}
                            pillClass="bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-300"
                            accentClass="accent-amber-500"
                        />
                    </div>

                    {/* ── Footer: page/copies info. Print moved to the action bar. ── */}
                    <div className="border-t border-[#F0EEEB] pt-3.5 dark:border-zinc-800">
                        <div className="flex items-center justify-between text-[11.5px] font-medium text-[#9A9AA3] dark:text-zinc-500">
                            <span>{t('passportToPdf.pageFooter')}</span>
                            <span>{copiesLabel}</span>
                        </div>
                    </div>
                    </div>
                </div>
            )}
            preview={(
                <PassportLivePreview
                    frontFile={frontFile}
                    backFile={backFile}
                    settings={previewSettings}
                    draggingSlot={draggingSlot}
                    onPick={openPicker}
                    onDrop={onDrop}
                    onDragOver={onDragOver}
                    onDragLeave={onDragLeave}
                    onCrop={onCropSlot}
                    onRemove={onRemoveSlot}
                    onSwap={onSwap}
                    onDuplicate={onDuplicate}
                    onRotate={onRotate}
                    onRotateSlot={onRotateSlot}
                    t={t}
                />
            )}
        />
        <PassportCropModal
            opened={Boolean(cropModal)}
            onClose={() => { abortBoothQueue(); closeCropModal(); }}
            imageSrc={cropModal?.src ?? null}
            title={cropModalTitle}
            fileName={cropModal?.file?.name}
            onApply={handleCropApply}
            busy={cropBusy}
        />
        </>
    );
}
